import { Injectable } from '@angular/core';
import { Actions, createEffect } from '@ngrx/effects';
import { concatMap, defer, EMPTY, from, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  fetchState,
  NGRX_STORAGE_SYNC_IGNORE_ACTIONS,
  saveState,
  StorageSyncActions,
} from './ngrx-ionic-storage-sync';
import { StorageService } from '@qld-recreational/storage';
import { Store } from '@ngrx/store';
import { activityLogFeatureKey } from '../activity-log/activity-log.reducer';
import { activityNoticesFeatureKey } from '../activity-notices/activity-notices.reducer';
import {
  amendingNoticeFeatureKey,
  amendingNoticePersistenceKeys,
} from '../amending-notice/amending-notice.reducer';
import { appStateFeatureKey } from '../app-state/app-state.reducer';
import { authFeatureKey, authPersistenceKeys } from '../auth/auth.reducer';
import {
  backgroundRequestPersistenceKeys,
  backgroundRequestsFeatureKey,
} from '../background-request/background-request.reducer';
import { catchDisposalRecordCatchesFeatureKey } from '../catch-disposal-record-catches/catch-disposal-record-catches.reducer';
import { catchDisposalRecordFeatureKey } from '../catch-disposal-record/catch-disposal-record.reducer';
import { contentfulFeatureKey } from '../contentful/contentful.reducer';
import { homeFeatureKey } from '../home/home.reducer';
import { logbookCatchesFeatureKey } from '../logbook-catches/logbook-catches.reducer';
import { logbookDaysFeatureKey } from '../logbook-days/logbook-days.reducer';
import { logbookEventsFeatureKey } from '../logbook-events/logbook-events.reducer';
import { logbooksFeatureKey } from '../logbooks/logbooks.reducer';
import { manualPollingFeatureKey } from '../manual-polling/manual-polling.reducer';
import { notificationsFeatureKey } from '../notification/notification.reducer';
import { preferencesCDRFishFormsMeasuresFeatureKey } from '../preference-cdr-fish-forms-measures/preference-cdr-fish-forms-measures.reducer';
import { preferencesConsignmentFeatureKey } from '../preference-consignment/preferences-consignment.reducer';
import { preferencesDisposalFeatureKey } from '../preference-disposal/preferences-disposal.reducer';
import { preferencesGPSFeatureKey } from '../preference-gps/preference-gps.reducer';
import { preferencesLandingPlacesFeatureKey } from '../preference-landing-places/preference-landing-places.reducer';
import {
  preferenceFeatureKey,
  preferenceFeaturePersistenceKeys,
} from '../preference/preference.reducer';
import { preferenceRegionsSpeciesFeatureKey } from '../preferences-regions-species/preferences-regions-species.reducer';
import { priorEmergencyNoticeFeatureKey } from '../prior-emergency-notice/prior-emergency-notice.reducer';
import { retainNoticeFeatureKey } from '../retain-notice/retain-notice.reducer';
import { settingsFeatureKey } from '../settings/settings.reducer';
import { tripFeatureKey } from '../trip/trip.reducer';
import {
  userBucketFeatureKey,
  userBucketPersistenceKeys,
} from '../user-bucket/user-bucket.reducer';
import { vmsFeatureKey } from '../vms/vms.reducer';
import { weightNoticeFeatureKey } from '../weight-notice/weight-notice.reducer';
import { tepSpeciesFeatureKey } from '../tep-species/tep-species.reducer';
import { SentryErrorHandler } from '../shared/sentry.service';

const keys = [
  { [authFeatureKey]: authPersistenceKeys },
  { [userBucketFeatureKey]: userBucketPersistenceKeys },
  logbooksFeatureKey,
  {
    [contentfulFeatureKey]: [
      'nextSyncToken',
      'contentfulContext',
      'targetEnv',
      'environments',
    ],
  },
  logbookDaysFeatureKey,
  logbookEventsFeatureKey,
  logbookCatchesFeatureKey,
  tripFeatureKey,
  { [preferenceFeatureKey]: preferenceFeaturePersistenceKeys },
  preferencesGPSFeatureKey,
  vmsFeatureKey,
  activityLogFeatureKey,
  homeFeatureKey,
  priorEmergencyNoticeFeatureKey,
  settingsFeatureKey,
  { [backgroundRequestsFeatureKey]: backgroundRequestPersistenceKeys },
  activityNoticesFeatureKey,
  weightNoticeFeatureKey,
  retainNoticeFeatureKey,
  catchDisposalRecordFeatureKey,
  notificationsFeatureKey,
  catchDisposalRecordCatchesFeatureKey,
  manualPollingFeatureKey,
  preferencesDisposalFeatureKey,
  preferencesConsignmentFeatureKey,
  { [amendingNoticeFeatureKey]: amendingNoticePersistenceKeys },
  appStateFeatureKey,
  preferenceRegionsSpeciesFeatureKey,
  preferencesLandingPlacesFeatureKey,
  preferencesCDRFishFormsMeasuresFeatureKey,
  tepSpeciesFeatureKey,
];

@Injectable()
export class StorageSyncEffects {
  constructor(
    private actions$: Actions,
    private storage: StorageService,
    private store: Store<{}>,
    private sentryService: SentryErrorHandler,
  ) {}

  private prevState: Record<string, Record<string, unknown>> | null = null;

  public saveState$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(
          (action) => !NGRX_STORAGE_SYNC_IGNORE_ACTIONS.includes(action.type),
        ),
        concatMap(() =>
          from(this.storage.init()).pipe(
            withLatestFrom(this.store.select((store) => store)),
            switchMap(([storage, nextState]) => {
              const prev = this.prevState;
              this.prevState = nextState;

              return from(saveState(this.storage, prev, nextState, keys)).pipe(
                catchError((err) => {
                  console.error('saveState error:', err);
                  this.sentryService.handleError(`saveState error: ${err}`);
                  return EMPTY;
                }),
              );
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  public hydrate$ = createEffect(() =>
    defer(() =>
      from(this.storage.init()).pipe(
        switchMap(() => {
          return from(fetchState(this.storage)).pipe(
            tap((state) => {
              this.prevState = state as Record<string, Record<string, unknown>>;
            }),
            map((state) => ({
              type: StorageSyncActions.HYDRATED,
              payload: state,
            })),
            catchError((e) => {
              console.warn(
                `error fetching data from storage for hydration: ${e}`,
              );
              this.sentryService.handleError(
                `error fetching data from storage for hydration: ${e}`,
              );

              return of({
                type: StorageSyncActions.HYDRATED,
                payload: {},
              });
            }),
          );
        }),
      ),
    ),
  );
}
